import type Phaser from 'phaser';
import type { Player } from '../types/game';
import { flagAssetPath, normalizeCountryCode } from './countryCatalog';

/**
 * Country Flag System V1 — Phaser texture plumbing for in-game player-card
 * flags. Uses the same vendored SVGs as the React shell (no PNG copies).
 *
 * Rasterization: Phaser's SVG loader rasterizes at load time, so each flag
 * is loaded at a fixed 64×48 (4:3) instead of its large intrinsic viewBox.
 * The in-card chip renders at ~18–24 CSS px wide (24 design units × k,
 * k ≈ 0.7–1.0 on typical desktops), so 64 px is ~2.5–3.5× the display
 * size — sharp without wasting texture memory, consistent with the settled
 * native-resolution-asset approach (no runtime HiDPI supersampling).
 *
 * Texture keys are deterministic (`country-flag-<code>`) and only ever
 * derived from allowlist-validated codes — server input can never inject an
 * arbitrary path or key. Textures live in the game-level TextureManager, so
 * they survive scene restarts (round transitions, reconnect) and are never
 * loaded twice.
 */

export const FLAG_TEXTURE_PREFIX = 'country-flag-';
export const FLAG_RASTER_WIDTH  = 64;
export const FLAG_RASTER_HEIGHT = 48;

/** Max distinct flags a single game can need (four seats). */
export const MAX_FLAGS_PER_GAME = 4;

/** Deterministic texture key for a supported code; null when unset/invalid. */
export function countryFlagTextureKey(raw: unknown): string | null {
  const code = normalizeCountryCode(raw);
  return code ? `${FLAG_TEXTURE_PREFIX}${code}` : null;
}

// In-flight guard: keys queued on the loader but not yet resolved. Repeated
// game states arriving while a load is still in flight (WS bursts, quick
// reconnects) must not enqueue the same file twice. Keys are removed on the
// batch's 'complete' (fires whether individual files succeeded or failed)
// and eagerly on each file's 'loaderror', so a failed flag can be retried
// by a later game state instead of being wedged as pending forever.
const pendingFlagKeys = new Set<string>();

/** Test-only: reset the in-flight set between test cases. */
export function __resetPendingFlagKeysForTests(): void {
  pendingFlagKeys.clear();
}

/**
 * Queue the distinct, validated flag textures needed by `players` (≤ 4) and
 * start the loader. Keys already in the texture cache OR already pending on
 * the loader are skipped, so round transitions, reconnects, and repeated
 * states during an in-flight load are all no-ops. `onLoaded` fires once
 * after a first-time load completes (never synchronously; not at all when
 * nothing needed loading) so the caller can redraw cards that were built
 * before the textures arrived. A file that fails to load never enters the
 * texture cache — the cards' textures.exists guard falls back to the
 * existing neutral presentation.
 */
export function ensureCountryFlagTextures(
  scene: Phaser.Scene,
  players: readonly Player[],
  onLoaded?: () => void,
): number {
  const codes = new Set<string>();
  for (const p of players) {
    const code = normalizeCountryCode(p.country_code);
    if (code) codes.add(code);
    if (codes.size >= MAX_FLAGS_PER_GAME) break;
  }

  const batchKeys: string[] = [];
  for (const code of codes) {
    const key = `${FLAG_TEXTURE_PREFIX}${code}`;
    if (scene.textures.exists(key)) {
      pendingFlagKeys.delete(key); // self-heal any stale pending entry
      continue;
    }
    if (pendingFlagKeys.has(key)) continue; // already in flight — never re-queue
    const path = flagAssetPath(code);
    if (!path) continue;
    pendingFlagKeys.add(key);
    batchKeys.push(key);
    scene.load.svg(key, path, { width: FLAG_RASTER_WIDTH, height: FLAG_RASTER_HEIGHT });
  }

  if (batchKeys.length === 0) return 0;

  const onError = (file: { key?: string } | undefined) => {
    if (file?.key) pendingFlagKeys.delete(file.key);
  };
  scene.load.on('loaderror', onError);
  scene.load.once('complete', () => {
    scene.load.off('loaderror', onError);
    for (const key of batchKeys) pendingFlagKeys.delete(key);
    onLoaded?.();
  });
  if (!scene.load.isLoading()) scene.load.start();
  return batchKeys.length;
}
